import { appendFragment, BaseDeeplinks, } from './base.js';
import { CUSTOMER_HANDLERS } from './modules/customer/index.js';
// Re-export this surface's modules so their param-map augmentations reach
// consumers via the emitted .d.ts (a value-only import is dropped from it).
export * from './modules/customer/index.js';
/**
 * Deeplinks for the operator surface: Hestia app ⇄ Hermes dashboard. Construct
 * once with the environment's Hermes base URL and (for link generators) the
 * target Hestia build's URL scheme.
 * @example
 * const deeplinks = new CustomerDeeplinks(env.HERMES_URL, 'owner-restaurant')
 * const { url, appLink } = deeplinks.buildUniversalLink(
 *   'inbox',
 *   { brandId, threadId },
 *   { locationId, query: { utm_source: 'push' } }
 * )
 */
export class CustomerDeeplinks extends BaseDeeplinks {
    /**
     * @param base Hermes dashboard origin, e.g. env.HERMES_URL.
     * @param scheme the target Hestia build's URL scheme without `://` (e.g.
     *   `owner-restaurant`); varies by env. Omit on ingest-only surfaces
     *   (`resolveInbound` / `buildNativePath`) — the `appLink` is null without it.
     */
    surface = 'customer';
    constructor(base, scheme) {
        super(base, CUSTOMER_HANDLERS, scheme);
    }
    buildAppLink(key, params, options) {
        const toNative = this.handlers[key].toNative;
        if (!toNative)
            return null;
        const locationId = options?.locationId;
        const needsLocation = this.scopedToLocationFor(key);
        if (needsLocation && !locationId)
            return null;
        return this.buildSchemeLink(toNative(params, locationId ?? ''), this.scopedQuery(key, 'app', options), options?.fragment);
    }
    /**
     * Whether a destination's native route needs a resolved location. An inbound
     * resolver (Hestia's `+native-intent`) must not demand one for a brand-scoped
     * link, whose route already carries the brand.
     * @param key registered deeplink key
     */
    isLocationScoped(key) {
        return this.scopedToLocationFor(key);
    }
    /**
     * Native Hestia route for a destination, given the resolved location.
     * @param key registered deeplink key
     * @param params typed params for that key
     * @param locationId resolved active location
     * @param fragment optional intra-page anchor (without `#`)
     * @returns the Hestia route path
     */
    buildNativePath(key, params, locationId, fragment) {
        const toNative = this.handlers[key].toNative;
        if (!toNative) {
            throw new Error(`Customer deeplink "${String(key)}" has no native route`);
        }
        return appendFragment(toNative(params, locationId), fragment);
    }
}
//# sourceMappingURL=customer.js.map